import {
    IGetLandingPage,
    ILandingPageModule,
    IMeta,
    IHeadTag,
    IModulesData,
    ILocaleConfig,
} from '@interfaces';
import { IRequest, IRequestCruiseSearchFilters } from '@interfaces/request';
import { objectKeys } from '../../../utils/object-keys';
import { __ } from '../../../utils/translation-service';

type IKeys = {
    [key in string]: string | null;
};

const getMinPrice = (modulesData: IModulesData): string | null => {
    const cheapestPrice = modulesData?.cruiseSearchlandingpageCheapestPrice;
    if (!cheapestPrice) return null;

    const minPrice = Number(((cheapestPrice?.items[0]?.sailings?.minPrice || 0) / 2).toFixed(0));
    return minPrice ? minPrice.toString() : null;
};

const getMaxDiscount = (modulesData: IModulesData): string | null => {
    const highestDiscount = modulesData?.cruiseSearchlandingpageHighestDiscount;
    if (!highestDiscount) return null;

    const maxDiscount = highestDiscount?.items[0]?.sailings?.maxDiscount;
    return maxDiscount ? `${__('upTo')} -${maxDiscount.toString()}%` : null;
};

const getName = (landingPage: IGetLandingPage): string | null => {
    return landingPage.title;
};

const getUGCTotalReviews = (modulesData: IModulesData): string | null => {
    const ugcData = modulesData[`ugcData`];
    const numRatings = ugcData?.aggregation?.data?.attributes?.numRatings;
    return numRatings ? numRatings.toString() : null;
};

const getDecks = (landingPage: IGetLandingPage, modulesData: IModulesData): string | null => {
    const shipData = modulesData[`ship${landingPage.id}`];
    const decks = shipData?.factsAndFigures?.decks;
    return decks ? decks.toString() : null;
};

const getPassengerCapacity = (
    landingPage: IGetLandingPage,
    modulesData: IModulesData,
): string | null => {
    const shipData = modulesData[`ship${landingPage.id}`];
    const passengerCapacity = shipData?.factsAndFigures?.passengerCapacity;
    return passengerCapacity ? passengerCapacity.toString() : null;
};

const getCabinTotalCategories = (
    landingPage: IGetLandingPage,
    modulesData: IModulesData,
): string | null => {
    const shipData = modulesData[`ship${landingPage.id}`];
    const numberOfCabins = shipData?.numberOfCabins;
    return numberOfCabins ? numberOfCabins.toString() : null;
};

const getShareImage = (localeConfig: ILocaleConfig): string => {
    switch (localeConfig.code) {
        case 'au':
            return 'https://images.cruiseaway.com.au/wp-content/uploads/20191128113244/dreamlines.jpg';
        default:
            return 'https://images.dreamlines.de/wp-content/uploads/20191128113244/dreamlines.jpg';
    }
};

const getKeys = (landingPage: IGetLandingPage, modulesData: IModulesData): IKeys => {
    return {
        discount: getMaxDiscount(modulesData),
        maxDiscount: getMaxDiscount(modulesData),
        minPrice: getMinPrice(modulesData),
        name: getName(landingPage),
        ugcTotalReviews: getUGCTotalReviews(modulesData),
        decks: getDecks(landingPage, modulesData),
        passengerCapacity: getPassengerCapacity(landingPage, modulesData),
        numberOfCabins: getCabinTotalCategories(landingPage, modulesData),
    };
};

export const getMetaTags = (props: IHeadTag, localeConfig: ILocaleConfig): IMeta => {
    switch (props.pageType) {
        case 'landingPage': {
            const { landingPage, modulesData } = props;
            const subPage = landingPage?.subpage;
            const subType = subPage?.type;
            const subPagesMeta = { ...landingPage?.meta?.subpages };
            let meta = { ...landingPage?.meta };

            if (meta && subPage && subPage.status && subType) {
                const subPageMeta = subPagesMeta[subType];
                if (subPagesMeta[subType]) {
                    meta.title = subPageMeta?.title ?? '';
                    meta.description = subPageMeta?.description ?? '';
                    meta.canonical = (subPage.slug && subPageMeta.canonical) ? subPageMeta.canonical : meta.canonical;
                }
            }

            meta.title = dataShortcodeReplacer(meta.title, landingPage, modulesData);
            meta.description = dataShortcodeReplacer(meta.description, landingPage, modulesData);
            meta.og.image.url = getShareImage(localeConfig);

            return meta;
        }

        default:
            const { title, description, canonical, robots, og } = props;
            og.image.url = getShareImage(localeConfig);
            return {
                title: title ?? '',
                description: description ?? '',
                canonical,
                robots,
                og,
            };
    }
};

export const dataShortcodeReplacer = (
    text: string,
    landingPage: IGetLandingPage,
    modulesData: IModulesData,
): string => {
    const dataKeys = getKeys(landingPage, modulesData);
    for (let key of objectKeys(dataKeys)) {
        const replaceText = dataKeys[key] || '';
        text = text.replace(`%${key}%`, replaceText);
    }
    return text;
};

export const getHeadTagsRequest = (args: {
    request: IRequest;
    component?: ILandingPageModule;
}): IRequest => {
    let { request } = args;
    let includeFilters: IRequestCruiseSearchFilters = {};
    switch (request.mainType) {
        case 'ship': {
            request = { ...request, ...shipRequest(request) };
            includeFilters.shipIds = [String(request.id)];
            break;
        }
        case 'cruiseline': {
            includeFilters.brandIds = [String(request.id)];
            break;
        }
        case 'destination': {
            includeFilters.regionIds = [String(request.id)];
            break;
        }
    }

    return {
        ...request,
        ...highestDiscountRequest(request, includeFilters),
        ...cheapestPriceRequest(request, includeFilters),
    };
};

const shipRequest = (request: IRequest) => {
    const index = request.ships.findIndex((item) => item.id === request.id);
    const ship = index === -1 ? { id: request.id, fields: [] } : request.ships[index];

    ship.fields.push('numberOfCabins', {
        name: 'factsAndFigures',
        fields: ['passengerCapacity', 'decks'],
    });

    if (index === -1) {
        request.ships.push(ship);
    } else {
        request.ships[index] = ship;
    }

    return request;
};

export const highestDiscountRequest = (
    request: IRequest,
    includeFilters: IRequestCruiseSearchFilters,
) => {
    request.cruiseSearches.push({
        name: 'HeadTagsHighestDiscount',
        includeFilters,
        limit: 1,
        sortBy: 'discount',
        fields: [
            {
                name: 'items',
                fields: [
                    {
                        name: 'sailings',
                        fields: ['maxDiscount'],
                    },
                ],
            },
        ],
    });

    return request;
};

export const cheapestPriceRequest = (
    request: IRequest,
    includeFilters: IRequestCruiseSearchFilters,
) => {
    request.cruiseSearches.push({
        name: 'HeadTagsCheapestPrice',
        includeFilters,
        limit: 1,
        sortBy: 'price_asc',
        fields: [
            {
                name: 'items',
                fields: [
                    {
                        name: 'sailings',
                        fields: ['minPrice'],
                    },
                ],
            },
        ],
    });

    return request;
};
